import React, { useEffect, useState, useCallback } from 'react';
import { FileJson, AlertCircle, RefreshCw } from 'lucide-react';
import { mqttClient } from '../lib/mqtt';

interface JsonData {
  updateNumber: number;
  timestamp: number;
  data: Record<string, any>;
}

export const JsonViewer: React.FC = () => {
  const [jsonData, setJsonData] = useState<JsonData[]>([]);
  const [selectedUser, setSelectedUser] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [updateCount, setUpdateCount] = useState<number>(0);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchJsonData = useCallback(async (userId: number) => {
    try {
      setIsLoading(true);
      const locationData = mqttClient.getAllLocationData();
      const userData = locationData[`user-${userId}`];
      
      if (!userData) {
        return;
      }

      const newData: JsonData = {
        updateNumber: updateCount + 1,
        timestamp: Date.now(),
        data: {
          ...userData,
          oddc: userData.oddc || "---" // ODDCの値がない場合は"---"を表示
        }
      };

      setJsonData(prev => {
        if (prev.length > 0 && 
            JSON.stringify(prev[prev.length - 1].data) === JSON.stringify(userData)) {
          return prev;
        }
        
        const updated = [...prev, newData].slice(-5);
        setUpdateCount(count => count + 1);
        setLastUpdate(new Date());
        return updated;
      });

      setError(null);
    } catch (error) {
      console.error('Error fetching JSON data:', error);
      setError(error instanceof Error ? error.message : 'データの取得中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  }, [updateCount]);

  useEffect(() => {
    fetchJsonData(selectedUser);
    const interval = setInterval(() => {
      fetchJsonData(selectedUser);
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedUser, fetchJsonData]);

  const getTimeDiff = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    if (diff < 1000) return '今';
    if (diff < 60000) return `${Math.floor(diff / 1000)}秒前`;
    return `${Math.floor(diff / 60000)}分前`;
  };

  return (
    <div className="fixed bottom-4 right-4 w-96">
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileJson className="w-5 h-5 text-blue-500" />
            <h3 className="font-semibold">JSON Viewer</h3>
          </div>
          <div className="flex items-center gap-4">
            {lastUpdate && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" />
                {getTimeDiff(lastUpdate.getTime())}
              </div>
            )}
            <select
              value={selectedUser}
              onChange={(e) => {
                setSelectedUser(Number(e.target.value));
                setJsonData([]);
                setError(null);
                setUpdateCount(0);
              }}
              className="px-2 py-1 border rounded"
              disabled={isLoading}
            >
              <option value={1}>ユーザー1</option>
              <option value={2}>ユーザー2</option>
            </select>
          </div>
        </div>
        
        {error ? (
          <div className="flex items-center gap-2 text-red-500 text-sm mb-2 p-4 bg-red-50 rounded">
            <AlertCircle className="w-4 h-4" />
            <div>
              <p className="font-medium">エラー</p>
              <p>{error}</p>
            </div>
          </div>
        ) : (
          <div className="max-h-60 overflow-y-auto">
            {isLoading && jsonData.length === 0 ? (
              <div className="text-gray-500 text-sm text-center py-4">
                読み込み中...
              </div>
            ) : jsonData.length === 0 ? (
              <div className="text-gray-500 text-sm text-center py-4">
                データがありません
              </div>
            ) : (
              jsonData.map((item, index) => (
                <div key={index} className="mb-2">
                  <div className="flex justify-between items-center mb-1 text-xs text-gray-500">
                    <span>更新 #{item.updateNumber}</span>
                    <span>{getTimeDiff(item.timestamp)}</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded text-sm font-mono">
                    <pre className="whitespace-pre-wrap break-all">
                      {JSON.stringify(item.data, null, 2)}
                    </pre>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};